import React from 'react'
import ReactDOM from 'react-dom'
import debounce from 'lodash/debounce'
import filter from 'lodash/filter'
import find from 'lodash/find'
import findIndex from 'lodash/findIndex'
import head from 'lodash/head'
import includes from 'lodash/includes'
import memoize from 'lodash/memoize'
import reduce from 'lodash/reduce'
import some from 'lodash/some'
import startsWith from 'lodash/startsWith'
import tail from 'lodash/tail'
import trim from 'lodash/trim'
// https://github.com/razagill/react-phone-input
import PropTypes from 'prop-types'
import { TextInputV2, l } from '@manychat/manyui'

import { allCountries } from './country_data'

let isModernBrowser = Boolean(document.createElement('input').setSelectionRange)

function isNumberValid(inputNumber) {
  let countries = [...allCountries]
  return some(countries, function (country) {
    return startsWith(inputNumber, country.dialCode) || startsWith(country.dialCode, inputNumber)
  })
}

function getOnlyCountries(onlyCountriesArray) {
  if (onlyCountriesArray.length === 0) {
    return allCountries
  } else {
    let selectedCountries = []
    allCountries.map(function (country) {
      onlyCountriesArray.map(function (selCountry) {
        if (country.iso2 === selCountry) {
          selectedCountries.push(country)
        }
      })
    })
    return selectedCountries
  }
}

function excludeCountries(selectedCountries, excludedCountries) {
  if (excludedCountries.length === 0) {
    return selectedCountries
  } else {
    return filter(selectedCountries, function (selCountry) {
      return !includes(excludedCountries, selCountry.iso2)
    })
  }
}

class ReactPhoneInput extends React.Component {
  constructor(props) {
    super(props)
    this.guessSelectedCountry = memoize(this._guessSelectedCountry)
    this._searchCountry = memoize(this.__searchCountry)

    let inputNumber = this.props.value || ''
    let onlyCountries = excludeCountries(
      getOnlyCountries(props.onlyCountries),
      props.excludeCountries,
    )
    let selectedCountryGuess = this.guessSelectedCountry(
      inputNumber.replace(/\D/g, ''),
      onlyCountries,
    )
    let selectedCountryGuessIndex = findIndex(allCountries, selectedCountryGuess)
    let dialCode =
      selectedCountryGuess &&
      !startsWith(inputNumber.replace(/\D/g, ''), selectedCountryGuess.dialCode)
        ? selectedCountryGuess.dialCode
        : ''
    let formattedNumber = this.formatNumber(
      dialCode + inputNumber.replace(/\D/g, ''),
      selectedCountryGuess ? selectedCountryGuess.format : null,
    )
    let preferredCountries = filter(allCountries, (country) => {
      return some(this.props.preferredCountries, (preferredCountry) => {
        return preferredCountry === country.iso2
      })
    })

    this.state = {
      preferredCountries: preferredCountries,
      selectedCountry: selectedCountryGuess,
      highlightCountryIndex: selectedCountryGuessIndex,
      formattedNumber: formattedNumber,
      showDropDown: false,
      queryString: '',
      freezeSelection: false,
      debouncedQueryStingSearcher: debounce(this.searchCountry, 100),
      onlyCountries: onlyCountries,
    }
  }

  getNumber = () => {
    return this.state.formattedNumber !== '+' ? this.state.formattedNumber : ''
  }

  getValue = () => {
    return this.getNumber()
  }

  resetNumber = () => {
    let formattedNumber = this.formatNumber(
      this.state.selectedCountry.dialCode,
      this.state.selectedCountry.format,
    )
    this.setState({
      formattedNumber: formattedNumber,
    })
  }

  componentDidMount() {
    document.addEventListener('keydown', this.handleKeydown)
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeydown)
  }

  formatNumber = (text, pattern) => {
    if (!text || text.length === 0) {
      return '+'
    }

    // for all strings with length less than 3, just return it (1, 2 etc.)
    // also return the same text if the selected country has no fixed format
    if ((text && text.length < 2) || !pattern || !this.props.autoFormat) {
      return `+${text}`
    }

    let formattedObject = reduce(
      pattern,
      function (acc, character) {
        if (acc.remainingText.length === 0) {
          return acc
        }

        if (character !== '.') {
          return {
            formattedText: acc.formattedText + character,
            remainingText: acc.remainingText,
          }
        }

        return {
          formattedText: acc.formattedText + head(acc.remainingText),
          remainingText: tail(acc.remainingText),
        }
      },
      { formattedText: '', remainingText: text.split('') },
    )
    return formattedObject.formattedText + formattedObject.remainingText.join('')
  }

  // put the cursor to the end of the input (usually after a focus event)
  _cursorToEnd = () => {
    let input = ReactDOM.findDOMNode(this.numberInputRef.current)
    input?.focus()
    if (isModernBrowser) {
      let len = input.value.length
      input?.setSelectionRange(len, len)
    }
  }

  handleInput = (event) => {
    let formattedNumber = '+',
      newSelectedCountry = this.state.selectedCountry,
      freezeSelection = this.state.freezeSelection

    //Does not exceed 16 digit phone number limit
    if (event.target.value.replace(/\D/g, '').length > 16) {
      return
    }

    // if the input is the same as before, must be some special key like enter etc.
    if (event.target.value === this.state.formattedNumber) {
      return
    }

    // ie hack
    if (event.preventDefault) {
      event.preventDefault()
    } else {
      event.returnValue = false
    }

    if (event.target.value.length > 0) {
      // before entering the number in new format, lets check if the dial code now matches some other country
      let inputNumber = event.target.value.replace(/\D/g, '')

      // we don't need to send the whole number to guess the country... only the first 6 characters are enough
      // the guess country function can then use memoization much more effectively since the set of input it
      // gets has drastically reduced
      if (
        !this.state.freezeSelection ||
        this.state.selectedCountry.dialCode.length > inputNumber.length
      ) {
        newSelectedCountry = this.guessSelectedCountry(
          inputNumber.substring(0, 6),
          this.state.onlyCountries,
        )
        freezeSelection = false
      }
      // let us remove all non numerals from the input
      formattedNumber = this.formatNumber(inputNumber, newSelectedCountry.format)
    }

    let caretPosition = event.target.selectionStart
    let oldFormattedText = this.state.formattedNumber
    let diff = formattedNumber.length - oldFormattedText.length

    this.setState(
      {
        formattedNumber: formattedNumber,
        freezeSelection: freezeSelection,
        selectedCountry:
          newSelectedCountry.dialCode.length > 0 ? newSelectedCountry : this.state.selectedCountry,
      },
      function () {
        if (isModernBrowser) {
          if (diff > 0) {
            caretPosition = caretPosition - diff
          }

          if (caretPosition > 0 && oldFormattedText.length >= formattedNumber.length) {
            ReactDOM.findDOMNode(this.numberInputRef.current).setSelectionRange(
              caretPosition,
              caretPosition,
            )
          }
        }

        if (this.props.onChange) {
          this.props.onChange({
            target: { name: this.props.name, value: this.state.formattedNumber },
          })
        }
      },
    )
  }

  handleInputFocus = (evt) => {
    // if the input is blank, insert dial code of the selected country
    if (ReactDOM.findDOMNode(this.numberInputRef.current).value === '+') {
      this.setState({ formattedNumber: '+' + this.state.selectedCountry.dialCode }, () =>
        setTimeout(this._cursorToEnd, 10),
      )
    }

    if (this.props.onFocus) {
      this.props.onFocus(evt)
    }
  }

  _getHighlightCountryIndex = (direction) => {
    // had to write own function because underscore does not have findIndex. lodash has it
    const highlightCountryIndex = this.state.highlightCountryIndex + direction

    if (
      highlightCountryIndex < 0 ||
      highlightCountryIndex >=
        this.state.onlyCountries.length + this.state.preferredCountries.length
    ) {
      return highlightCountryIndex - direction
    }

    return highlightCountryIndex
  }

  searchCountry = () => {
    const probableCandidate =
      this._searchCountry(this.state.queryString) || this.state.onlyCountries[0]
    const probableCandidateIndex =
      findIndex(this.state.onlyCountries, probableCandidate) + this.state.preferredCountries.length

    this.setState({
      queryString: '',
      highlightCountryIndex: probableCandidateIndex,
    })
  }

  _guessSelectedCountry = (inputNumber, onlyCountries) => {
    let bestGuess
    const secondBestGuess =
      find(allCountries, { iso2: this.props.defaultCountry }) || onlyCountries[0]
    if (trim(inputNumber) !== '') {
      bestGuess = reduce(
        onlyCountries,
        function (selectedCountry, country) {
          if (startsWith(inputNumber, country.dialCode)) {
            if (country.dialCode.length > selectedCountry.dialCode.length) {
              return country
            }
            if (
              country.dialCode.length === selectedCountry.dialCode.length &&
              country.priority < selectedCountry.priority
            ) {
              return country
            }
          }

          return selectedCountry
        },
        { dialCode: '', priority: 10001 },
        this,
      )
    } else {
      return secondBestGuess
    }

    if (!bestGuess.name) {
      return secondBestGuess
    }

    return bestGuess
  }

  __searchCountry = (queryString) => {
    if (!queryString || queryString.length === 0) {
      return null
    }
    // don't include the preferred countries in search
    let probableCountries = filter(
      this.state.onlyCountries,
      function (country) {
        return startsWith(country.name.toLowerCase(), queryString.toLowerCase())
      },
      this,
    )
    return probableCountries[0]
  }

  focus = () => {
    this._cursorToEnd()
  }

  numberInputRef = React.createRef()

  render() {
    const { placeholder, className, id, name, invalid, onBlur, onKeyDown } = this.props

    return (
      <TextInputV2
        onChange={this.handleInput}
        onKeyDown={onKeyDown}
        onFocus={this.handleInputFocus}
        onBlur={onBlur}
        value={this.state.formattedNumber}
        inputRef={this.numberInputRef}
        type="tel"
        ariaLabel={l.translate('Enter phone number')}
        placeholder={placeholder}
        className={className}
        name={name}
        id={id}
        invalid={invalid}
      />
    )
  }
}

ReactPhoneInput.defaultProps = {
  value: '',
  autoFormat: true,
  onlyCountries: [],
  excludeCountries: [],
  defaultCountry: allCountries[0].iso2,
  isValid: isNumberValid,
}

ReactPhoneInput.propTypes = {
  value: PropTypes.string,
  autoFormat: PropTypes.bool,
  defaultCountry: PropTypes.string,
  onlyCountries: PropTypes.arrayOf(PropTypes.string),
  preferredCountries: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onClick: PropTypes.func,
  onKeyDown: PropTypes.func,
}

export default ReactPhoneInput
